import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import PropTypes from 'prop-types';
// @mui
import { useTheme } from '@mui/material/styles';
import { Alert, Box, CSSObject, Snackbar, Stack, Typography } from '@mui/material';
import { GoBackLink } from './Buttons';
import { useNavigate } from 'react-router-dom';
import { WarningDialog } from 'components/WarningDialog';
import { getError, setError } from 'state/reducers/pending-requests.reducer';
import { useMemo } from 'react';
import WaitingMessage from 'components/WaitingMessage';
import { selectTranslations } from 'state/reducers/translation.reducers';
import _ from 'lodash';
import { setSuccessMessage } from 'state/reducers/ui.reducers';

// ----------------------------------------------------------------------

interface IProps {
	children?: PropTypes.ReactElementLike | PropTypes.ReactElementLike[];
	style?: CSSObject;
	title?: string;
	actions?: any;
	subTitle?: any;
	includeGoBack?: boolean;
	goBackUrl?: string;
	loading?: boolean;
}

export const Page = (props: IProps) => {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const { title, actions, subTitle, includeGoBack, goBackUrl, loading } = props;
	const navigate = useNavigate();
	const pendingRequestError = useAppSelector(getError);
	const showSuccess = useAppSelector((state) => state.ui.success);
	const dispatch = useAppDispatch();
	const openErrorDialog = useMemo(() => {
		return pendingRequestError.exist;
	}, [pendingRequestError]);
	const openSuccessSnackbar = useMemo(() => {
		return !!showSuccess;
	}, [showSuccess]);

	return (
		<Box
			sx={{
				...props.style,
				position: 'relative',
				height: '100%',
				background: theme.palette.background.paper,
				padding: '32px',
				animation: 'Effect .6s',
				'@keyframes Effect': {
					'0%': {
						opacity: 0,
					},
					'100%': {
						opacity: 1,
					},
				},
			}}
		>
			{title && (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						marginBottom: theme.spacing(5),
					}}
				>
					<Stack spacing={2} sx={{ width: '-webkit-fill-available' }}>
						<Typography variant="pageTitle" display={'flex'} flexDirection={'row'} gap={2}>
							{includeGoBack && (
								<GoBackLink
									onClick={() => {
										const url = goBackUrl ? goBackUrl : -1;
										navigate(url);
									}}
								>
									<img src={'/icons/arrow-back.svg'} />
								</GoBackLink>
							)}
							{title}
						</Typography>
						<Box>{subTitle}</Box>
					</Stack>
					{actions && actions}
				</Box>
			)}
			{loading ? <WaitingMessage /> : props.children}
			<WarningDialog
				open={openErrorDialog || false}
				subTitle={pendingRequestError?.text}
				handleCancel={() => {
					dispatch(setError({ exist: false }));
				}}
			/>
			<Snackbar
				open={openSuccessSnackbar}
				autoHideDuration={10000}
				onClose={() => dispatch(setSuccessMessage(false))}
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				sx={{
					right: '24px!important',
					left: 'auto',
					'& .MuiAlert-root': {
						background: theme.palette.root.orange,
						marginLeft: theme.sizes.DRAWER_WIDTH + 'px',
						border: '1px solid #85A09E',
						borderRadius: '8px',
						width: 'auto',
						color: '#fff',
						height: '38px',
						'& > div': {
							color: '#fff',
							padding: 0,
							'& button': {
								padding: '2px 2px 2px 16px',
							},
						},
						'& .MuiAlert-message': {
							fontSize: '16px',
							overflow: 'hidden',
							alignItems: 'center',
							display: 'flex',
						},
					},
				}}
			>
				<Alert
					onClose={() => dispatch(setSuccessMessage(false))}
					severity="success"
					variant="filled"
					sx={{ width: '100%' }}
				>
					{t(_.toString(showSuccess))}
				</Alert>
			</Snackbar>
		</Box>
	);
};
